import React from 'react';
import { useTranscriptionProgress } from '../../hooks/useTranscriptionProgress';
import { AudioFile } from '../../types';
import { formatDuration } from '../../utils/formatters';
import { AudioWaveLoadingIcon } from '../../icons';

interface TranscriptionProgressProps {
  audioFile: AudioFile;
}

const TranscriptionProgress: React.FC<TranscriptionProgressProps> = ({
  audioFile,
}) => {
  const progress = useTranscriptionProgress(audioFile);

  if (!progress) {
    // Fallback to simple loading state if no progress data
    return (
      <div className="flex items-center">
        <AudioWaveLoadingIcon
          size={20}
          className="-ml-1 mr-3 text-yellow-600"
        />
        <span className="text-yellow-800 dark:text-yellow-200">
          Processing transcription...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center">
          <AudioWaveLoadingIcon size={16} className="mr-2 text-yellow-600" />
          <span className="text-yellow-800 dark:text-yellow-200">
            Processing transcription...
          </span>
        </div>
        <span className="text-yellow-700 dark:text-yellow-300 font-medium">
          {progress.percentage}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className="absolute top-0 left-0 h-full bg-yellow-500 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress.percentage}%` }}
        />
        {/* Add a subtle animation for the active processing */}
        {!progress.isOverdue && (
          <div
            className="absolute top-0 h-full w-1/4 bg-gradient-to-r from-transparent via-yellow-400 to-transparent opacity-30 animate-pulse"
            style={{
              left: `${Math.max(0, progress.percentage - 10)}%`,
            }}
          />
        )}
      </div>

      {/* Time information */}
      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
        <span>Elapsed: {formatDuration(progress.elapsedSeconds)}</span>
        <span>
          {progress.isOverdue ? (
            <span className="text-orange-600 dark:text-orange-400">
              Taking longer than expected...
            </span>
          ) : (
            `Remaining: ~${formatDuration(progress.remainingSeconds)}`
          )}
        </span>
      </div>

      {/* Additional info for debugging (can be removed in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="text-xs text-gray-500 dark:text-gray-500 pt-1 border-t border-gray-200 dark:border-gray-700">
          Estimated total: {formatDuration(progress.estimatedTotalSeconds)} |
          File size: {audioFile.fileSizeDisplay}
          {audioFile.durationSeconds &&
            ` | Duration: ${audioFile.durationDisplay}`}
        </div>
      )}
    </div>
  );
};

export default TranscriptionProgress;
